import { DatePicker } from "antd";
import locale from "antd/locale/fr_FR";
import classnames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdCheck,
  MdPriorityHigh,
  MdLock,
  MdOutlineKeyboard,
} from "react-icons/md";
import Label from "shared/components/Label";
import useStableDatePickerValue from "shared/hooks/useStableDatePickerValue";
import useStrictDatePickerInput from "shared/hooks/useStrictDatePickerInput";
import { NAME_SPACES } from "shared/locales/constants";
import { DATE_FORMAT } from "utils/constants";

const Simple = ({
  label,
  errors,
  icon,
  touched,
  props,
  direction,
  required,
  optional,
  right,
  success,
  showSuccessIcon = true,
  placeholder,
  className,
  format,
  onChange,
  onBlur,
  value,
  disabledDate,
  ...rest
}) => {
  const { t } = useTranslation(NAME_SPACES.COMPONENTS);
  const DATE_PICKER = t("DATE_PICKER", { returnObjects: true });
  const resolvedFormat = format || DATE_FORMAT;

  const [clickCount, setClickCount] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [hasManualValue, setHasManualValue] = useState(false);

  const {
    inputError,
    applyMask,
    handleCalendarChange,
    handleDateChange,
    handleBlur,
    handleKeyDown,
  } = useStrictDatePickerInput({
    format: resolvedFormat,
    invalidDateMessage: DATE_PICKER.INVALID_DATE,
    disallowedDateMessage: DATE_PICKER.DISALLOWED_DATE,
    disabledDate,
    onChange,
    onBlur,
    value,
  });

  const handleCalendarClick = () => {
    setClickCount((prevCount) => prevCount + 1);
  };

  useEffect(() => {
    if (clickCount >= 3) {
      setShowHint(true);
    }
  }, [clickCount]);

  useEffect(() => {
    // Ant Design portals the panel to document.body, so query the document
    // only while this instance's calendar is open.
    if (!isCalendarOpen) return undefined;

    const datePickerButtons = document.querySelectorAll(".ant-picker-header");
    datePickerButtons.forEach((button) => {
      button.addEventListener("click", handleCalendarClick);
    });

    return () => {
      datePickerButtons.forEach((button) => {
        button.removeEventListener("click", handleCalendarClick);
      });
    };
  }, [isCalendarOpen]);

  const handleOpenChange = (open) => {
    setIsCalendarOpen(open);
    if (!open) {
      setClickCount(0);
      setShowHint(false);
    }
    if (typeof rest.onOpenChange === "function") {
      rest.onOpenChange(open);
    }
  };

  const hasValue = !!value;
  const isFilled = hasValue || hasManualValue;
  const stableValue = useStableDatePickerValue(value);
  const displayError = inputError || errors;
  const showError = (touched || !!inputError) && displayError;

  useEffect(() => {
    setHasManualValue(!!value);
  }, [value]);

  const handleDateChangeWithManualState = (nextValue, dateString) => {
    handleDateChange(nextValue, dateString);
    setHasManualValue(!!nextValue || !!dateString);
  };

  const handleInputBlur = (event) => {
    handleBlur(event);
    setHasManualValue(!!event?.target?.value?.trim());
  };

  const handleInputKeyUp = (event) => {
    applyMask(event);
    setHasManualValue(!!event?.target?.value?.trim());
    if (rest.onKeyUp) rest.onKeyUp(event);
  };

  const handleInputKeyDown = (event) => {
    applyMask(event);
    handleKeyDown(event);
    if (rest.onKeyDown) rest.onKeyDown(event);
  };

  return (
    <div
      className={classnames(
        "datepicker__wrapper",
        {
          __error: showError,
          __success: success && !displayError,
        },
        direction,
      )}
    >
      <Label
        label={label}
        required={required}
        optional={optional}
        right={right}
      />
      <div
        className={classnames("datepicker__wrapper--field", {
          "datepicker__wrapper--field--filled": isFilled,
        })}
      >
        {icon && <span className="datepicker__icon">{icon}</span>}
        <DatePicker
          locale={locale}
          placeholder={DATE_PICKER.PLACEHOLDER}
          suffixIcon={null}
          {...props}
          className={classnames(className, {
            "disabled--input": rest.disabled,
            "datepicker--with-icon": !!icon,
            "datepicker--filled": isFilled,
          })}
          {...rest}
          format={resolvedFormat}
          disabledDate={disabledDate}
          value={stableValue}
          onChange={handleDateChangeWithManualState}
          onCalendarChange={handleCalendarChange}
          onBlur={handleInputBlur}
          onKeyUp={handleInputKeyUp}
          onKeyDown={handleInputKeyDown}
          onOpenChange={handleOpenChange}
          renderExtraFooter={() =>
            showHint && (
              <div className="datePicker-hint-text">
                <MdOutlineKeyboard size={24} />
                <span>{DATE_PICKER.HINT}</span>
              </div>
            )
          }
        />

        {showError && (
          <MdPriorityHigh className="input--error-icon" size={20} />
        )}

        {success && showSuccessIcon && !displayError && (
          <MdCheck className="input--success-icon" size={20} />
        )}

        {rest.disabled && !showError && (
          <MdLock className="input--disabled-icon" size={16} />
        )}
      </div>
      {showError && <p className={"input--errors"}>{displayError}</p>}
    </div>
  );
};

export default Simple;
